import { Alert, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconRocket } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, officeApi } from '../api.js';
import { ErrorAlert } from './ErrorAlert.js';

interface Props {
  vessel: { id: string; name: string } | null;
  onClose: () => void;
  onDone: () => void;
}

/**
 * "Kullanıma al" onayı (ADR 0142 §2): kurulumdaki etkin gemi ofisin açık kararıyla canlıya geçer.
 * Geri dönüş yok; bundan sonra ofisin açtığı klasör kaldırılmaz (emekliye ayrılır), PMS'i ofis
 * yine gemiye gönderir (Ek 2026-10-31).
 */
export function GoLiveModal({ vessel, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (vessel === null) setError(null);
  }, [vessel]);

  const confirm = async () => {
    if (!vessel) return;
    setBusy(true);
    setError(null);
    try {
      await officeApi.goLive(vessel.id);
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.code : 'GENERIC');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={vessel !== null}
      onClose={onClose}
      title={t('fleet.goLive.title', { vessel: vessel?.name ?? '' })}
      closeButtonProps={{ 'aria-label': t('fleet.closeDialog') }}
      centered
    >
      <Stack gap="md">
        <Text fz="sm">{t('fleet.goLive.text')}</Text>
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
          <Text fz="sm">{t('fleet.goLive.warning')}</Text>
        </Alert>
        <ErrorAlert code={error} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>
            {t('fleet.goLive.cancel')}
          </Button>
          <Button leftSection={<IconRocket size={16} />} loading={busy} onClick={confirm}>
            {t('fleet.goLive.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
